import { html } from 'lit';

import { renderAdnAd } from '@client/adnuntius/renderAd';
import { AdVendor } from '@client/core/AdVendor';
import { atom } from '@podium/store';
import { events, messageBus } from '@schibsted-nmp/advertising-events';
import {
  AdConfig,
  AdnPlacement,
  AdServer,
  PlacementId
} from '@schibsted-nmp/advertising-shared';

export enum AtomChannels {
  Advertising = 'advertising'
}
export enum AtomTopics {
  BatchRequestAdQue = 'batchRequestAdQue',
  RequestedPlacements = 'requestedPlacements',
  RenderedPlacements = 'renderedPlacements'
}

export const batchRequestAd = (placementId: PlacementId) =>
  !$requestedPlacements.value?.includes(placementId) &&
  $batchRequestAdQueue.set([
    ...($batchRequestAdQueue.value ?? []),
    placementId
  ]);

export const $batchRequestAdQueue = atom<PlacementId[]>(
  AtomChannels.Advertising,
  AtomTopics.BatchRequestAdQue,
  []
);

export const $requestedPlacements = atom<PlacementId[]>(
  AtomChannels.Advertising,
  AtomTopics.RequestedPlacements,
  []
);

export class AdnAdVendor extends AdVendor {
  placementId: PlacementId;

  constructor(placementId: PlacementId) {
    super();
    this.adServer = AdServer.Adn;
    this.placementId = placementId;
    this.containerId = `${placementId}--container`;
  }

  cleanupEvents(): void {}

  updated(): void {}

  setupEvents({ handleAdTypeChange, handleStatusChange }): void {
    const eventCallback = (event) => {
      const payload = events.PODLET.AD_CONTAINER_STATUS.getPayload(event);
      if (payload.containerId === this.containerId) {
        // Apply styles and classes to the host element
        handleAdTypeChange(payload.adType);
        handleStatusChange(payload.status);
      }
    };
    const peekEvent = messageBus.peek(
      events.PODLET.channel,
      events.PODLET.AD_CONTAINER_STATUS.topic
    );
    if (peekEvent) {
      eventCallback(peekEvent);
    }
    this.cleanupEvents = messageBus.subscribe(
      events.PODLET.channel,
      events.PODLET.AD_CONTAINER_STATUS.topic,
      eventCallback
    );
  }

  requestAd(): void {
    renderAdnAd({
      placementId: this.placementId,
      containerId: this.containerId || `${this.placementId}--container`
    });
  }

  render() {
    return html`
      <slot name="${`${this.placementId}--slot`}" id="${this.placementId}">
        >
        <div id="${this.placementId}"></div
      ></slot>
    `;
  }

  static initAdQueue(config: AdConfig) {
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const adnPlacements = config.placements.filter(
      (placement) => placement.adServer.type === AdServer.Adn
    ) as AdnPlacement[];

    $batchRequestAdQueue.subscribe((placementIds) => {
      if (placementIds.length === 0) return;

      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      debounceTimer = setTimeout(() => {
        const adUnits = placementIds.map((placementId) => ({
          auId:
            adnPlacements.find(
              (placement) => placement.placementId === placementId
            )?.adServer.config.auId ?? '',
          targetId: placementId,
          kv: config.adServer.adn?.kv || []
        }));

        window.adn.calls.push(() => {
          window.adn.request({
            adUnits
          });
        });

        // Resets que & sets ads to requested
        $batchRequestAdQueue.set([]);
        placementIds.forEach((placementId) => {
          $requestedPlacements.set([
            ...($requestedPlacements.value ?? []),
            placementId
          ]);
        });
      }, 200);
    });
  }
}
